import { getOperatorOptions } from '../logicHelpers'

type DataLayerCondition = { $source: string; $type: string; operator?: string; key?: string }

const isType = (condition: DataLayerCondition, type: 'value' | 'event') =>
  condition.$source === 'datalayer' && condition.$type === type

const valueOperators = () => [
  ...getOperatorOptions(),
  { label: 'Greater than', value: 'gt' },
  { label: 'Less than', value: 'lt' },
  { label: 'Greater than or equal to', value: 'gte' },
  { label: 'Less than or equal to', value: 'lte' }
]

/** Google Tag Manager's `window.dataLayer` (ENG-1617): merged values and pushed events. */
const datalayerSource = () => ({
  types: [
    {
      label: 'Data layer value',
      value: 'value',
      description: 'A value pushed to the GTM dataLayer, such as ecommerce.value or user.plan'
    },
    {
      label: 'Data layer event',
      value: 'event',
      description: 'An event pushed to the GTM dataLayer, such as purchase or form_submit'
    }
  ],
  inputs: [
    {
      control: 'text',
      key: 'datalayer-variable-input',
      name: 'variable',
      placeholder: 'ecommerce.value',
      can: (condition: DataLayerCondition) => isType(condition, 'value')
    },
    {
      control: 'select',
      key: 'datalayer-value-operator-select',
      name: 'operator',
      options: valueOperators(),
      can: (condition: DataLayerCondition) => isType(condition, 'value')
    },
    {
      control: 'text',
      key: 'datalayer-value-input',
      name: 'value',
      can: (condition: DataLayerCondition) =>
        isType(condition, 'value') && condition.operator !== 'is set' && condition.operator !== 'is not set'
    },
    {
      control: 'text',
      key: 'datalayer-event-input',
      name: 'event',
      placeholder: 'purchase',
      can: (condition: DataLayerCondition) => isType(condition, 'event')
    },
    {
      control: 'select',
      key: 'datalayer-event-operator-select',
      name: 'operator',
      options: [
        { label: 'Has fired', value: 'has_fired' },
        { label: 'Has not fired', value: 'has_not_fired' }
      ],
      can: (condition: DataLayerCondition) => isType(condition, 'event')
    },
    {
      control: 'select',
      key: 'datalayer-event-within-select',
      name: 'within',
      options: [
        { label: 'on this page view', value: 'pageview' },
        { label: 'this session', value: 'session' }
      ],
      can: (condition: DataLayerCondition) => isType(condition, 'event')
    },
    {
      control: 'content',
      key: 'datalayer-event-where-content',
      content: 'where',
      can: (condition: DataLayerCondition) => isType(condition, 'event')
    },
    {
      control: 'text',
      key: 'datalayer-event-key-input',
      name: 'key',
      placeholder: 'key, optional (form_id)',
      can: (condition: DataLayerCondition) => isType(condition, 'event')
    },
    {
      control: 'content',
      key: 'datalayer-event-equals-content',
      content: 'equals',
      can: (condition: DataLayerCondition) => isType(condition, 'event') && Boolean(condition.key?.trim())
    },
    {
      control: 'text',
      key: 'datalayer-event-value-input',
      name: 'value',
      placeholder: 'any value',
      can: (condition: DataLayerCondition) => isType(condition, 'event') && Boolean(condition.key?.trim())
    }
  ]
})

export default datalayerSource
